/**
 * Code Generation Store - Replaces CodeGeneratorAgent Durable Object
 * Manages code generation state with IndexedDB persistence
 */

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { browserDB } from '../services/browser-storage/indexedDB';
import type { AIMessage } from '../services/ai/client-ai';
import { clientAI } from '../services/ai/client-ai';
import { getFileType } from '@/utils/string';
import type { FileType, PhaseTimelineItem } from '../routes/chat/hooks/use-chat';
import type { BlueprintType } from '@/api-types';

export interface CodeGenState {
  // Current session
  agentId: string | null;
  projectId: number | null;

  // Generation state
  isGenerating: boolean;
  isGeneratingBlueprint: boolean;
  isBootstrapping: boolean;
  isThinking: boolean;
  currentPhase: string | null;

  // Data
  blueprint: BlueprintType | null;
  blueprintMarkdown: string; // Streaming blueprint text for UI display
  researchMarkdown: string;
  hasResearch: boolean;
  planningMarkdown: string;
  hasPlanning: boolean;
  architectureMarkdown: string;
  hasArchitecture: boolean;
  files: FileType[];
  bootstrapFiles: FileType[];
  phaseTimeline: PhaseTimelineItem[];
  totalFiles: number | null;
  
  // Preview
  previewUrl: string | null;
  isPreviewDeploying: boolean;
  shouldRefreshPreview: boolean;
  
  // Deployment
  isDeploying: boolean;
  cloudflareDeploymentUrl: string;
  deploymentError: string | null;
  isRedeployReady: boolean;
  
  // Control
  isGenerationPaused: boolean;
  isPhaseProgressActive: boolean;
  
  // Messages and conversation
  messages: AIMessage[];
  query: string | null;
}

export interface CodeGenActions {
  // Session management
  initializeSession: (agentId: string, query: string) => Promise<void>;
  loadExistingSession: (agentId: string) => Promise<void>;
  clearSession: () => void;
  
  // Generation control
  startGeneration: () => Promise<void>;
  stopGeneration: () => void;
  resumeGeneration: () => Promise<void>;
  pauseGeneration: () => void;
  
  // Blueprint & Research & Planning & Architecture
  setBlueprint: (blueprint: BlueprintType) => void;
  runResearch: (query?: string) => Promise<void>;
  runPlanning: (query?: string) => Promise<void>;
  runArchitecture: (query?: string) => Promise<void>;
  generateBlueprint: (query: string) => Promise<void>;

  // Files
  addFile: (file: FileType) => void;
  updateFile: (filePath: string, updates: Partial<FileType>) => void;
  removeFile: (filePath: string) => void;
  setFiles: (files: FileType[]) => void;
  addBootstrapFiles: (files: FileType[]) => void;
  
  // Phase management
  addPhase: (phase: PhaseTimelineItem) => void;
  updatePhase: (phaseId: string, updates: Partial<PhaseTimelineItem>) => void;
  setCurrentPhase: (phase: string) => void;
  
  // Preview
  setPreviewUrl: (url: string) => void;
  setPreviewDeploying: (deploying: boolean) => void;
  refreshPreview: () => void;
  
  // Deployment
  deployToCloudflare: () => Promise<void>;
  setDeploymentUrl: (url: string) => void;
  setDeploymentError: (error: string | null) => void;
  setRedeployReady: (ready: boolean) => void;
  
  // State updates
  setGenerating: (generating: boolean) => void;
  setGeneratingBlueprint: (generating: boolean) => void;
  setBootstrapping: (bootstrapping: boolean) => void;
  setThinking: (thinking: boolean) => void;
  setPhaseProgressActive: (active: boolean) => void;
  setTotalFiles: (total: number) => void;
  
  // Messages
  addMessage: (message: AIMessage) => void;
  setMessages: (messages: AIMessage[]) => void;
  
  // Persistence
  saveState: () => Promise<void>;
  loadState: () => Promise<void>;
}

export type CodeGenStore = CodeGenState & CodeGenActions;

const initialState: CodeGenState = {
  agentId: null,
  projectId: null,
  isGenerating: false,
  isGeneratingBlueprint: false,
  isBootstrapping: true,
  isThinking: false,
  currentPhase: null,
  blueprint: null,
  blueprintMarkdown: '',
  researchMarkdown: '',
  hasResearch: false,
  planningMarkdown: '',
  hasPlanning: false,
  architectureMarkdown: '',
  hasArchitecture: false,
  files: [],
  bootstrapFiles: [],
  phaseTimeline: [],
  totalFiles: null,
  previewUrl: null,
  isPreviewDeploying: false,
  shouldRefreshPreview: false,
  isDeploying: false,
  cloudflareDeploymentUrl: '',
  deploymentError: null,
  isRedeployReady: false,
  isGenerationPaused: false,
  isPhaseProgressActive: false,
  messages: [],
  query: null
};

export const useCodeGenStore = create<CodeGenStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,

    // Session management
    initializeSession: async (agentId: string, query: string) => {
      console.log('[DEBUG] initializeSession called:', {
        timestamp: new Date().toISOString(),
        agentId,
        query: query.substring(0, 100) + (query.length > 100 ? '...' : '')
      });

      set({ agentId, query, isBootstrapping: true });

      // Circuit breaker: if a project for this agent already exists, reuse it
      const existing = await browserDB.getProjectByAgentId(agentId);
      console.log('[DEBUG] initializeSession: Existing project check:', {
        found: !!existing?.id,
        projectId: existing?.id
      });

      if (existing?.id) {
        console.log('[DEBUG] initializeSession: Circuit breaker - reusing existing project');
        set({ projectId: existing.id });
        await get().saveState();
        return;
      }

      // Create project in database
      console.log('[DEBUG] initializeSession: Creating new project');
      const project = await browserDB.createProject({
        userId: 1, // TODO: Get from auth store
        agentId,
        title: query,
        description: query,
        status: 'active'
      });

      console.log('[DEBUG] initializeSession: New project created:', {
        projectId: project.id,
        agentId
      });

      set({ projectId: project.id! });
      await get().saveState();
    },

    loadExistingSession: async (agentId: string) => {
      const project = await browserDB.getProjectByAgentId(agentId);
      if (!project) {
        throw new Error(`Project not found for agent: ${agentId}`);
      }

      set({ 
        agentId, 
        projectId: project.id!,
        query: project.title,
        isBootstrapping: false 
      });

      // Load existing code generations
      const codeGens = await browserDB.getCodeGenerationsByProjectId(project.id!);
      const allFiles: FileType[] = [];
      const timeline: PhaseTimelineItem[] = [];

      for (const codeGen of codeGens) {
        const files = JSON.parse(codeGen.files) as FileType[];
        allFiles.push(...files);
        
        timeline.push({
          id: codeGen.id!.toString(),
          name: codeGen.phase,
          description: `Phase: ${codeGen.phase}`,
          files: files.map(f => ({
            path: f.filePath,
            purpose: f.explanation || '',
            status: codeGen.status === 'completed' ? 'completed' : 'generating',
            contents: f.fileContents
          })),
          status: codeGen.status === 'completed' ? 'completed' : 'generating',
          timestamp: codeGen.createdAt.getTime()
        });
      }

      set({ files: allFiles, phaseTimeline: timeline });
      
      if (project.blueprint) {
        set({ blueprint: JSON.parse(project.blueprint) });
      }

      await get().saveState();
    },

    clearSession: () => {
      set(initialState);
    },

    // Generation control
    startGeneration: async () => {
      console.log('[codeGenStore.startGeneration] ENTRY - agentId:', get().agentId, 'projectId:', get().projectId);

      const chooseProvider = async (): Promise<{ provider: 'nanogpt' | 'google-ai-studio' | 'openrouter' | 'openai'; model: string }> => {
        if (await clientAI.hasApiKey('nanogpt')) return { provider: 'nanogpt', model: 'z-ai/glm-4.6' };
        if (await clientAI.hasApiKey('google-ai-studio')) return { provider: 'google-ai-studio', model: 'models/gemma-3-27b-it' };
        if (await clientAI.hasApiKey('openrouter')) return { provider: 'openrouter', model: 'openrouter/auto' };
        if (await clientAI.hasApiKey('openai')) return { provider: 'openai', model: 'gpt-4o-mini' };
        throw new Error('No AI provider API key configured. Please add NanoGPT, Google AI Studio, OpenRouter, or OpenAI API key in Settings.');
      };

      const extractCode = (raw: string): string => {
        const trimmed = raw.trim();
        if (trimmed.startsWith('```')) {
          return trimmed.replace(/^```[a-zA-Z]*\n|```$/g, '').trim();
        }
        return trimmed;
      };

      const buildFilePrompt = (filePath: string, phase: { id: string; name: string; description: string }): string => {
        const state = get();
        const bp = state.blueprint;
        const existing = state.files.map(f => `- ${f.filePath}`).join('\n');
        const purpose = Array.isArray((bp as any)?.fileStructure)
          ? ((bp as any).fileStructure as any[]).find((f: any) => f.path === filePath)?.purpose || ''
          : typeof (bp as any)?.fileStructure === 'object'
            ? (bp as any).fileStructure?.[filePath]?.purpose || ''
            : '';
        const deps = (bp as any)?.dependencies ? Object.keys((bp as any).dependencies).join(', ') : '';
        return `Generate ${filePath} for a React + TypeScript app.\n\nProject: ${bp?.title}\nDescription: ${bp?.description}\nCurrent Phase: ${phase.name}\n\nFile Purpose: ${purpose}\nDependencies Available: ${deps}\n\nExisting Files (do not duplicate):\n${existing}\n\nRequirements:\n- Use React, hooks, and TypeScript\n- Prefer Tailwind CSS if styling is needed\n- Export defaults where typical\n- Return ONLY the file content, no markdown fences.`;
      };

      try {
        const state = get();
        if (!state.blueprint) throw new Error('Blueprint not found. Generate a blueprint before starting code generation.');

        console.log('[codeGenStore.startGeneration] 🔄 Setting generation state...');
        set({ isGenerating: true, isGenerationPaused: false, isPhaseProgressActive: true });
        await get().saveState();

        let phases: Array<{ id: string; name: string; description: string; files: string[] }> = [];
        if (Array.isArray((state.blueprint as any).implementationRoadmap)) {
          phases = (state.blueprint as any).implementationRoadmap!.map((p: any, idx: number) => ({
            id: p.phase || p.name || `phase_${idx + 1}`,
            name: p.name || p.phase || `Phase ${idx + 1}`,
            description: p.description || '',
            files: Array.isArray(p.files) ? p.files.map((f: any) => (typeof f === 'string' ? f : f.path)) : []
          }));
        } else if (Array.isArray(state.phaseTimeline) && state.phaseTimeline.length > 0) {
          phases = state.phaseTimeline.map((p) => ({ id: p.id, name: p.name, description: p.description, files: p.files.map(f => f.path) }));
        }

        const { provider, model } = await chooseProvider();

        for (const phase of phases) {
          set({ currentPhase: phase.id });
          get().updatePhase(phase.id, { status: 'generating' });
          await get().saveState();

          for (const filePath of phase.files) {
            const tl = get().phaseTimeline;
            const updated = tl.map(p => p.id === phase.id ? {
              ...p,
              files: p.files.map(f => f.path === filePath ? { ...f, status: 'generating' as const } : f)
            } : p);
            set({ phaseTimeline: updated });

            const prompt = buildFilePrompt(filePath, { id: phase.id, name: phase.name, description: phase.description });

            // Initialize file with empty content for streaming
            get().addFile({
              filePath,
              fileContents: '',
              language: getFileType(filePath),
              isGenerating: true
            });

            let fileContent = '';

            // Stream the file generation
            for await (const chunk of clientAI.generateStreamingResponse({
              provider,
              model,
              maxTokens: 4000,
              messages: [
                { role: 'system', content: 'You are an expert TypeScript+React code generator. Output only the file code.' },
                { role: 'user', content: prompt }
              ]
            })) {
              if (chunk.done) {
                console.log('[codeGenStore.startGeneration] ✅ File streaming completed for:', filePath);
                break;
              }

              fileContent += chunk.content;

              // Update file content in real-time
              get().updateFile(filePath, {
                fileContents: fileContent,
                isGenerating: true
              });

              console.log('[codeGenStore.startGeneration] 📝 File streaming chunk for', filePath, 'length:', fileContent.length);
            }

            const content = extractCode(fileContent);

            // Update final file content and mark as completed
            get().updateFile(filePath, {
              fileContents: content,
              isGenerating: false
            });

            const tl2 = get().phaseTimeline;
            const updated2 = tl2.map(p => p.id === phase.id ? {
              ...p,
              files: p.files.map(f => f.path === filePath ? { ...f, status: 'completed' as const, contents: content } : f)
            } : p);
            set({ phaseTimeline: updated2 });
            await get().saveState();
          }

          get().updatePhase(phase.id, { status: 'completed' });
          await get().saveState();
        }

        set({ isGenerating: false, isPhaseProgressActive: false });
        await get().saveState();
        console.log('[codeGenStore.startGeneration] ✅ Completed successfully');
      } catch (error) {
        console.error('[codeGenStore.startGeneration] ❌ FAILED:', error);
        set({ isGenerating: false, isPhaseProgressActive: false });
        throw error;
      }
    },

    stopGeneration: () => {
      set({ isGenerating: false, isGenerationPaused: false });
    },

    resumeGeneration: async () => {
      set({ isGenerating: true, isGenerationPaused: false });
      await get().saveState();
    },

    pauseGeneration: () => {
      set({ isGenerationPaused: true });
    },

    // Blueprint
    setBlueprint: (blueprint: BlueprintType) => {
      set({ blueprint });
      get().saveState();
    },

    runResearch: async (query?: string) => {
      try {
        const q = query ?? get().query ?? '';
        if (!q) return;
        // Mark phase and timeline
        const phaseId = 'research';
        set({ currentPhase: phaseId, isPhaseProgressActive: true, researchMarkdown: '' });
        const tl = get().phaseTimeline;
        if (!tl.find(p => p.id === phaseId)) {
          get().addPhase({
            id: phaseId,
            name: 'Research',
            description: 'Deep research on requirements and prior art',
            files: [],
            status: 'generating',
            timestamp: Date.now()
          });
        } else {
          get().updatePhase(phaseId, { status: 'generating' });
        }

        const chooseProvider = async (): Promise<{ provider: 'nanogpt' | 'google-ai-studio' | 'openrouter' | 'openai'; model: string }> => {
          if (await clientAI.hasApiKey('nanogpt')) return { provider: 'nanogpt', model: 'z-ai/glm-4.6' };
          if (await clientAI.hasApiKey('google-ai-studio')) return { provider: 'google-ai-studio', model: 'models/gemma-3-27b-it' };
          if (await clientAI.hasApiKey('openrouter')) return { provider: 'openrouter', model: 'openrouter/auto' };
          if (await clientAI.hasApiKey('openai')) return { provider: 'openai', model: 'gpt-4o-mini' };
          throw new Error('No AI provider API key configured. Please add NanoGPT, Google AI Studio, OpenRouter, or OpenAI API key in Settings.');
        };

        const { provider, model } = await chooseProvider();
        const prompt = `You are a senior engineer conducting pre-planning research for a web app request.\n\nUser request: ${q}\n\nTasks:\n1) Identify similar OSS/projects and their approaches (name + link).\n2) Recommend frameworks/libraries with rationale.\n3) Outline architectural options with trade-offs.\n4) List key risks/pitfalls and mitigations.\n5) Propose evaluation criteria that we will use in Planning.\n\nReturn well-structured Markdown with concise bullet points and links.`;

        let md = '';
        for await (const chunk of clientAI.generateStreamingResponse({
          provider,
          model,
          maxTokens: 1200,
          messages: [
            { role: 'system', content: 'You produce rigorous, actionable engineering research in Markdown.' },
            { role: 'user', content: prompt }
          ]
        })) {
          if (chunk.done) break;
          md += chunk.content;
          set({ researchMarkdown: md });
        }

        set({ hasResearch: true });
        get().updatePhase(phaseId, { status: 'completed', description: 'Research completed' });
        set({ isPhaseProgressActive: false });
        await get().saveState();
      } catch (error) {
        console.error('[codeGenStore.runResearch] FAILED:', error);
        set({ isPhaseProgressActive: false });
        throw error;
      }
    },

    runPlanning: async (query?: string) => {
      try {
        const q = query ?? get().query ?? '';
        if (!q) return;

        // Mark phase and timeline
        const phaseId = 'planning';
        set({ currentPhase: phaseId, isPhaseProgressActive: true, planningMarkdown: '' });
        const tl = get().phaseTimeline;
        if (!tl.find(p => p.id === phaseId)) {
          get().addPhase({
            id: phaseId,
            name: 'Planning',
            description: 'Creating comprehensive technical specifications',
            files: [],
            status: 'generating',
            timestamp: Date.now()
          });
        } else {
          get().updatePhase(phaseId, { status: 'generating' });
        }

        // Get research context if available
        const researchContext = get().hasResearch ? `\n\nPrevious Research:\n${get().researchMarkdown}` : '';

        const chooseProvider = async (): Promise<{ provider: 'nanogpt' | 'google-ai-studio' | 'openrouter' | 'openai'; model: string }> => {
          if (await clientAI.hasApiKey('nanogpt')) return { provider: 'nanogpt', model: 'z-ai/glm-4.6' };
          if (await clientAI.hasApiKey('google-ai-studio')) return { provider: 'google-ai-studio', model: 'models/gemma-3-27b-it' };
          if (await clientAI.hasApiKey('openrouter')) return { provider: 'openrouter', model: 'openrouter/auto' };
          if (await clientAI.hasApiKey('openai')) return { provider: 'openai', model: 'gpt-4o-mini' };
          throw new Error('No AI provider API key configured. Please add NanoGPT, Google AI Studio, OpenRouter, or OpenAI API key in Settings.');
        };

        const { provider, model } = await chooseProvider();
        const prompt = `You are a senior technical architect creating comprehensive planning documentation for a web application.

User request: ${q}${researchContext}

Create a detailed planning document that includes:

## 1. Technical Specifications
- Core functionality requirements
- Performance requirements
- Security considerations
- Scalability requirements

## 2. System Architecture
- High-level architecture diagram (text-based)
- Component breakdown
- Data flow description
- Integration points

## 3. API Design
- REST API endpoints (if applicable)
- Request/response schemas
- Authentication/authorization strategy
- Rate limiting and caching

## 4. Data Models
- Database schema design
- Entity relationships
- Data validation rules
- Migration strategy

## 5. User Experience Flow
- User journey mapping
- Key user interactions
- UI/UX considerations
- Accessibility requirements

## 6. Implementation Strategy
- Development phases breakdown
- Technology stack rationale
- Third-party integrations
- Testing strategy

Return well-structured Markdown with clear sections, bullet points, and technical details.`;

        let md = '';
        for await (const chunk of clientAI.generateStreamingResponse({
          provider,
          model,
          maxTokens: 2000,
          messages: [
            { role: 'system', content: 'You are a senior technical architect who creates comprehensive, actionable planning documentation.' },
            { role: 'user', content: prompt }
          ]
        })) {
          if (chunk.done) break;
          md += chunk.content;
          set({ planningMarkdown: md });
        }

        set({ hasPlanning: true });
        get().updatePhase(phaseId, { status: 'completed', description: 'Planning documentation completed' });
        set({ isPhaseProgressActive: false });
        await get().saveState();
      } catch (error) {
        console.error('[codeGenStore.runPlanning] FAILED:', error);
        set({ isPhaseProgressActive: false });
        throw error;
      }
    },

    runArchitecture: async (query?: string) => {
      try {
        const q = query ?? get().query ?? '';
        if (!q) return;

        // Mark phase and timeline
        const phaseId = 'architecture';
        set({ currentPhase: phaseId, isPhaseProgressActive: true, architectureMarkdown: '' });
        const tl = get().phaseTimeline;
        if (!tl.find(p => p.id === phaseId)) {
          get().addPhase({
            id: phaseId,
            name: 'Architecture',
            description: 'Creating detailed task breakdown and implementation strategy',
            files: [],
            status: 'generating',
            timestamp: Date.now()
          });
        } else {
          get().updatePhase(phaseId, { status: 'generating' });
        }

        // Get previous context if available
        const researchContext = get().hasResearch ? `\n\nPrevious Research:\n${get().researchMarkdown}` : '';
        const planningContext = get().hasPlanning ? `\n\nPlanning Documentation:\n${get().planningMarkdown}` : '';

        const chooseProvider = async (): Promise<{ provider: 'nanogpt' | 'google-ai-studio' | 'openrouter' | 'openai'; model: string }> => {
          if (await clientAI.hasApiKey('nanogpt')) return { provider: 'nanogpt', model: 'z-ai/glm-4.6' };
          if (await clientAI.hasApiKey('google-ai-studio')) return { provider: 'google-ai-studio', model: 'models/gemma-3-27b-it' };
          if (await clientAI.hasApiKey('openrouter')) return { provider: 'openrouter', model: 'openrouter/auto' };
          if (await clientAI.hasApiKey('openai')) return { provider: 'openai', model: 'gpt-4o-mini' };
          throw new Error('No AI provider API key configured. Please add NanoGPT, Google AI Studio, OpenRouter, or OpenAI API key in Settings.');
        };

        const { provider, model } = await chooseProvider();
        const prompt = `You are a senior software architect creating a detailed implementation strategy and task breakdown.

User request: ${q}${researchContext}${planningContext}

Create a comprehensive architecture document that includes:

## 1. Implementation Phases
- Phase breakdown with clear deliverables
- Dependencies between phases
- Estimated effort and timeline
- Risk assessment for each phase

## 2. Task Breakdown Structure
- Detailed task list for each phase
- Task dependencies and prerequisites
- Acceptance criteria for each task
- Priority levels (Critical, High, Medium, Low)

## 3. Technical Architecture
- System components and their interactions
- Data flow and state management
- API design and integration points
- Security and performance considerations

## 4. Development Strategy
- Technology stack decisions with rationale
- Code organization and structure
- Testing strategy and quality gates
- Deployment and DevOps considerations

## 5. Risk Mitigation
- Identified technical risks
- Mitigation strategies
- Fallback plans
- Quality assurance measures

Return well-structured Markdown with clear sections, numbered tasks, and actionable items.`;

        let md = '';
        for await (const chunk of clientAI.generateStreamingResponse({
          provider,
          model,
          maxTokens: 2500,
          messages: [
            { role: 'system', content: 'You are a senior software architect who creates detailed, actionable implementation strategies and task breakdowns.' },
            { role: 'user', content: prompt }
          ]
        })) {
          if (chunk.done) break;
          md += chunk.content;
          set({ architectureMarkdown: md });
        }

        set({ hasArchitecture: true });
        get().updatePhase(phaseId, { status: 'completed', description: 'Architecture and task breakdown completed' });
        set({ isPhaseProgressActive: false });
        await get().saveState();
      } catch (error) {
        console.error('[codeGenStore.runArchitecture] FAILED:', error);
        set({ isPhaseProgressActive: false });
        throw error;
      }
    },

    generateBlueprint: async (query: string) => {
      console.log('[codeGenStore.generateBlueprint] ENTRY - query:', query, 'agentId:', get().agentId);

      set({ isGeneratingBlueprint: true, query });

      // Helper: choose first provider with a configured API key
      const chooseProvider = async (): Promise<{ provider: 'nanogpt' | 'google-ai-studio' | 'openrouter' | 'openai'; model: string }> => {
        if (await clientAI.hasApiKey('nanogpt')) return { provider: 'nanogpt', model: 'z-ai/glm-4.6' };
        if (await clientAI.hasApiKey('google-ai-studio')) return { provider: 'google-ai-studio', model: 'models/gemma-3-27b-it' };
        if (await clientAI.hasApiKey('openrouter')) return { provider: 'openrouter', model: 'openrouter/auto' };
        if (await clientAI.hasApiKey('openai')) return { provider: 'openai', model: 'gpt-4o-mini' };
        throw new Error('No AI provider API key configured. Please add NanoGPT, Google AI Studio, OpenRouter, or OpenAI API key in Settings.');
      };

      // Helper: strip markdown fences and trailing text
      const toJsonString = (raw: string): string => {
        const fenced = raw.trim().replace(/^```[a-zA-Z]*\n|```$/g, '');
        // If multiple fence blocks or prose wrap JSON, try to find the first JSON object/array
        const start = fenced.indexOf('{');
        const arrStart = fenced.indexOf('[');
        const idx = (start === -1) ? arrStart : (arrStart === -1 ? start : Math.min(start, arrStart));
        return idx >= 0 ? fenced.slice(idx) : fenced;
      };

      try {
        const { provider, model } = await chooseProvider();
        const blueprintPrompt = `Analyze this webapp request and create a detailed blueprint in JSON.\n\nUser Request: ${query}\n\nReturn ONLY valid JSON with these keys:\n- title: string\n- description: string\n- techStack: string[]\n- dependencies: Record<string,string>\n- fileStructure: Array<{ path: string; purpose: string; dependencies?: string[] }>\n- phases: Array<{ id: string; name: string; description: string; files: string[] }>`;

        // Initialize streaming state
        set({ blueprintMarkdown: '' });
        let blueprintText = '';

        // Stream the blueprint generation
        for await (const chunk of clientAI.generateStreamingResponse({
          provider,
          model,
          maxTokens: 1500,
          messages: [
            { role: 'system', content: 'You are a senior fullstack engineer who outputs strictly valid JSON when asked.' },
            { role: 'user', content: blueprintPrompt }
          ]
        })) {
          if (chunk.done) {
            console.log('[codeGenStore.generateBlueprint] ✅ Streaming completed');
            break;
          }

          blueprintText += chunk.content;
          set({ blueprintMarkdown: blueprintText });
          console.log('[codeGenStore.generateBlueprint] 📝 Streaming chunk received, total length:', blueprintText.length);
        }

        // Create a mock response object for compatibility with existing code
        const res = { content: blueprintText };

        const jsonText = toJsonString(res.content);
        let parsed: any;
        try {
          parsed = JSON.parse(jsonText);
        } catch (e) {
          console.warn('[codeGenStore.generateBlueprint] JSON parse failed; raw content:', res.content);
          throw new Error('Blueprint model did not return valid JSON');
        }

        // Map to BlueprintType (using existing flexible fields)
        const blueprint: BlueprintType = {
          title: parsed.title || query,
          description: parsed.description || `Blueprint for ${query}`,
          techStack: parsed.techStack || [],
          frameworks: parsed.techStack || [],
          fileStructure: Array.isArray(parsed.fileStructure)
            ? Object.fromEntries(parsed.fileStructure.map((f: any) => [f.path, { purpose: f.purpose, dependencies: f.dependencies || [] }]))
            : (parsed.fileStructure || {}),
          implementationRoadmap: Array.isArray(parsed.phases)
            ? parsed.phases.map((p: any) => ({
                phase: p.id || p.name,
                name: p.name,
                description: p.description,
                files: (p.files || []).map((path: string) => ({ path }))
              }))
            : undefined,
        };

        // Build initial phase timeline from phases
        const phases: Array<{ id: string; name: string; description: string; files: string[] }> = Array.isArray(parsed.phases)
          ? parsed.phases
          : [];

        const phaseTimeline: PhaseTimelineItem[] = phases.map((p: any, idx: number) => ({
          id: p.id || `phase_${idx + 1}`,
          name: p.name || `Phase ${idx + 1}`,
          description: p.description || '',
          files: (p.files || []).map((path: string) => ({
            path,
            purpose: parsed.fileStructure?.find?.((f: any) => f.path === path)?.purpose || '',
            status: 'generating' as const
          })),
          status: 'generating',
          timestamp: Date.now()
        }));

        const totalFiles = phases.reduce((acc: number, p: any) => acc + (Array.isArray(p.files) ? p.files.length : 0), 0);

        set({ blueprint, isGeneratingBlueprint: false, phaseTimeline, totalFiles });
        await get().saveState();
        console.log('[codeGenStore.generateBlueprint] ✅ Completed successfully');
      } catch (error) {
        console.error('[codeGenStore.generateBlueprint] ❌ FAILED:', error);
        set({ isGeneratingBlueprint: false });
        throw error;
      }
    },

    // Files
    addFile: (file: FileType) => {
      set(state => ({ files: [...state.files, file] }));
      get().saveState();
    },

    updateFile: (filePath: string, updates: Partial<FileType>) => {
      set(state => ({
        files: state.files.map(file => 
          file.filePath === filePath ? { ...file, ...updates } : file
        )
      }));
      get().saveState();
    },

    removeFile: (filePath: string) => {
      set(state => ({
        files: state.files.filter(file => file.filePath !== filePath)
      }));
      get().saveState();
    },

    setFiles: (files: FileType[]) => {
      set({ files });
      get().saveState();
    },

    addBootstrapFiles: (files: FileType[]) => {
      set(state => ({ bootstrapFiles: [...state.bootstrapFiles, ...files] }));
      get().saveState();
    },

    // Phase management
    addPhase: (phase: PhaseTimelineItem) => {
      set(state => ({ phaseTimeline: [...state.phaseTimeline, phase] }));
      get().saveState();
    },

    updatePhase: (phaseId: string, updates: Partial<PhaseTimelineItem>) => {
      set(state => ({
        phaseTimeline: state.phaseTimeline.map(phase =>
          phase.id === phaseId ? { ...phase, ...updates } : phase
        )
      }));
      get().saveState();
    },

    setCurrentPhase: (phase: string) => {
      set({ currentPhase: phase });
    },

    // Preview
    setPreviewUrl: (url: string) => {
      set({ previewUrl: url });
    },

    setPreviewDeploying: (deploying: boolean) => {
      set({ isPreviewDeploying: deploying });
    },

    refreshPreview: () => {
      set({ shouldRefreshPreview: true });
      // Reset after a short delay
      setTimeout(() => set({ shouldRefreshPreview: false }), 100);
    },

    // Deployment
    deployToCloudflare: async () => {
      set({ isDeploying: true, deploymentError: null });
      
      try {
        // TODO: Implement actual deployment logic
        // This would involve packaging files and deploying to Cloudflare
        
        // Placeholder
        await new Promise(resolve => setTimeout(resolve, 2000));
        set({ 
          isDeploying: false, 
          cloudflareDeploymentUrl: 'https://example.cloudflare.dev',
          isRedeployReady: true 
        });
      } catch (error) {
        set({ 
          isDeploying: false, 
          deploymentError: error instanceof Error ? error.message : 'Deployment failed' 
        });
      }
    },

    setDeploymentUrl: (url: string) => {
      set({ cloudflareDeploymentUrl: url });
    },

    setDeploymentError: (error: string | null) => {
      set({ deploymentError: error });
    },

    setRedeployReady: (ready: boolean) => {
      set({ isRedeployReady: ready });
    },

    // State updates
    setGenerating: (generating: boolean) => {
      set({ isGenerating: generating });
    },

    setGeneratingBlueprint: (generating: boolean) => {
      set({ isGeneratingBlueprint: generating });
    },

    setBootstrapping: (bootstrapping: boolean) => {
      set({ isBootstrapping: bootstrapping });
    },

    setThinking: (thinking: boolean) => {
      set({ isThinking: thinking });
    },

    setPhaseProgressActive: (active: boolean) => {
      set({ isPhaseProgressActive: active });
    },

    setTotalFiles: (total: number) => {
      set({ totalFiles: total });
    },

    // Messages
    addMessage: (message: AIMessage) => {
      set(state => ({ messages: [...state.messages, message] }));
    },

    setMessages: (messages: AIMessage[]) => {
      set({ messages });
    },

    // Persistence
    saveState: async () => {
      const state = get();
      if (!state.projectId) return;

      // Update project with current state
      await browserDB.updateProject(state.projectId, {
        title: state.query || 'Untitled Project',
        description: state.query ?? undefined,
        blueprint: state.blueprint ? JSON.stringify(state.blueprint) : undefined,
        status: state.isGenerating ? 'active' : 'completed'
      });

      // Save current files as a code generation entry
      if (state.files.length > 0) {
        await browserDB.createCodeGeneration({
          projectId: state.projectId,
          phase: state.currentPhase || 'main',
          files: JSON.stringify(state.files),
          status: state.isGenerating ? 'generating' : 'completed'
        });
      }
    },

    loadState: async () => {
      const state = get();
      if (!state.agentId) return;

      await get().loadExistingSession(state.agentId);
    }
  }))
);

// Auto-save state changes
useCodeGenStore.subscribe(
  (state) => state,
  (state) => {
    // Debounce saves to avoid too frequent database writes
    const timeoutId = setTimeout(() => {
      state.saveState().catch(console.error);
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }
);
